import clsx from "clsx";
import {
  CheckCircle2,
  Clock,
  FileText,
  XCircle,
  type LucideIcon,
} from "lucide-react";

import { Card } from "../../../shared/card/card.tsx";
import type { ApprovalCenterStats as ApprovalCenterStatsData } from "../schemas/phase2-list.schema.ts";

import styles from "./approval-center-stats.module.css";

export interface ApprovalCenterStatsProps {
  stats: ApprovalCenterStatsData;
  loading?: boolean;
  activeStatus?: string;
  onSelect: (status: string | undefined) => void;
  approvedOnly?: boolean;
  isSqe?: boolean;
}

interface Tile {
  key: "total" | "approved" | "pendingReview" | "pendingInsurance" | "pending" | "pendingPreJoining" | "rejected";
  status?: string;
  label: string;
  subtitle: string;
  icon: LucideIcon;
  iconClass: string;
}

export function ApprovalCenterStats({
  stats,
  loading,
  activeStatus,
  onSelect,
  approvedOnly = false,
  isSqe = false,
}: ApprovalCenterStatsProps) {
  const regularTiles: Tile[] = [
    {
      key: "total",
      label: "Total Requests",
      subtitle: "All time requests",
      icon: FileText,
      iconClass: styles.iconPurple,
    },
    {
      key: "approved",
      status: "APPROVED",
      label: "Approved",
      subtitle: "Successfully approved",
      icon: CheckCircle2,
      iconClass: styles.iconGreen,
    },
    {
      key: "pendingReview",
      status: "PENDING_REVIEW",
      label: "Pending Review",
      subtitle: "Documents or bank details await review",
      icon: Clock,
      iconClass: styles.iconAmber,
    },
    {
      key: "pendingInsurance",
      status: "PENDING_INSURANCE",
      label: "Pending Insurance",
      subtitle: "Insurance has not been added",
      icon: Clock,
      iconClass: styles.iconAmber,
    },
  ];
  const sqeTiles: Tile[] = [
    regularTiles[0],
    {
      key: "pending",
      status: "PENDING",
      label: "Pending Post-Joining",
      subtitle: "Awaiting post-joining",
      icon: Clock,
      iconClass: styles.iconAmber,
    },
    {
      key: "pendingPreJoining",
      status: "PENDING_PREJOINING",
      label: "Pending Pre-Joining",
      subtitle: "Awaiting pre-joining",
      icon: Clock,
      iconClass: styles.iconAmber,
    },
    regularTiles[1],
    {
      key: "rejected",
      status: "REJECTED",
      label: "Rejected",
      subtitle: "Requests rejected",
      icon: XCircle,
      iconClass: styles.iconRed,
    },
  ];
  const tiles = isSqe ? sqeTiles : regularTiles;

  return (
    <div className={clsx(styles.grid, isSqe && styles.gridSqe)} aria-busy={loading ? true : undefined}>
      {tiles.map(({ key, status, label, subtitle, icon: Icon, iconClass }) => (
        <Card key={key} className={clsx(styles.card, activeStatus === status && styles.selected)} padding="none" variant="default">
          <button
            type="button"
            className={styles.cardButton}
            onClick={() => onSelect(status)}
            disabled={approvedOnly && status !== "APPROVED"}
            aria-label={`Show ${label.toLowerCase()} requests`}
            aria-pressed={activeStatus === status}
            title={approvedOnly && status !== "APPROVED" ? "Finance access is limited to approved requests" : subtitle}
          >
            <span className={clsx(styles.iconWrap, iconClass)} aria-hidden>
              <Icon size={20} strokeWidth={2} />
            </span>
            <div className={styles.text}>
              <p className={clsx("sub-heading-xs", styles.label)}>{label}</p>
              <p className={clsx("heading-lg", styles.value)}>
                {loading ? "—" : stats[key].toLocaleString()}
              </p>
            </div>
          </button>
        </Card>
      ))}
    </div>
  );
}
